import React, { useMemo, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { AffiliateCommissionMode, UpdateUserAffiliatesBulkPayload } from "services/user/type"
import { userService } from "services/user/user.service"

import { formatFiatCurrency, isValidPercent, parseIntSafe } from "util/formatter"

type Props = {
  userId?: string
  onUpdated: () => void
}

export const UserAffiliateBulkEditor = ({ userId, onUpdated }: Props) => {
  const [mode, setMode] = useState<AffiliateCommissionMode | "">("")
  const [returnPercent, setReturnPercent] = useState("")

  const [cpaRewardCents, setCpaRewardCents] = useState("")
  const [cpaRequireKyc, setCpaRequireKyc] = useState<"any" | "yes" | "no">("any")
  const [cpaMinDepositCents, setCpaMinDepositCents] = useState("")
  const [cpaMinWagerCents, setCpaMinWagerCents] = useState("")

  const [saving, setSaving] = useState(false)

  const percentError = useMemo(() => {
    if (!returnPercent.trim()) return null
    return isValidPercent(returnPercent) ? null : "return_percent must be between 0 and 100"
  }, [returnPercent])

  const rewardError = useMemo(() => {
    if (mode !== AffiliateCommissionMode.CPA) return null
    if (!cpaRewardCents.trim()) return null
    const n = Number(cpaRewardCents)
    if (!Number.isFinite(n) || n < 0) return "reward_cents must be >= 0"
    return null
  }, [mode, cpaRewardCents])

  const patch = useMemo((): UpdateUserAffiliatesBulkPayload => {
    const bulkPatch: UpdateUserAffiliatesBulkPayload = {}

    if (mode) bulkPatch.commission_mode = mode

    if (mode !== AffiliateCommissionMode.CPA) {
      if (returnPercent.trim()) {
        const n = Number(returnPercent)
        if (!Number.isNaN(n)) bulkPatch.return_percent = n
      }
      return bulkPatch
    }

    const cfg: any = {}
    const reward = parseIntSafe(cpaRewardCents)
    const minDep = parseIntSafe(cpaMinDepositCents)
    const minWager = parseIntSafe(cpaMinWagerCents)

    if (reward !== null) cfg.reward_cents = reward
    if (cpaRequireKyc !== "any") cfg.require_kyc = cpaRequireKyc === "yes"
    if (minDep !== null) cfg.min_total_deposit_cents = minDep
    if (minWager !== null) cfg.min_total_wager_cents = minWager

    if (Object.keys(cfg).length) {
      bulkPatch.cpa_config = cfg
    }

    return bulkPatch
  }, [mode, returnPercent, cpaRewardCents, cpaRequireKyc, cpaMinDepositCents, cpaMinWagerCents])

  const hasAny = useMemo(() => {
    const hasMode = patch.commission_mode !== undefined

    const hasReturn = patch.return_percent !== undefined
    const hasCpaCfg = patch.cpa_config !== undefined

    return hasMode || hasReturn || hasCpaCfg
  }, [patch])

  const canApply = !!userId && !saving && hasAny && !percentError && !rewardError

  const apply = async () => {
    if (!userId) return
    if (!hasAny) return toast.error("Nothing to update")
    if (percentError) return toast.error(percentError)
    if (rewardError) return toast.error(rewardError)

    try {
      setSaving(true)
      const res = await userService.updateUserAffiliatesBulk(userId, patch)
      const updated = res?.payload?.updated ?? res?.updated
      toast.success(`Updated ${updated ?? ""} affiliate code(s)`)

      setMode("")
      setReturnPercent("")

      setCpaRewardCents("")
      setCpaRequireKyc("any")
      setCpaMinDepositCents("")
      setCpaMinWagerCents("")

      onUpdated()
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to bulk update affiliates")
    } finally {
      setSaving(false)
    }
  }

  const isCpaMode = mode === AffiliateCommissionMode.CPA

  return (
    <div className="rounded-xl border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-2">
          <div className="text-base font-semibold">Affiliate settings (bulk)</div>
          <div className="text-sm font-normal opacity-60">Update all affiliate codes for this user</div>
        </div>

        <button
          type="button"
          className={clsx("btn btn-primary btn-sm", { "btn-disabled": !canApply })}
          onClick={apply}
          disabled={!canApply}
        >
          {saving ? <span className="loading loading-spinner loading-xs" /> : null}
          Apply to all
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-sm opacity-80">Commission mode</label>

          <select
            className="select select-bordered w-full"
            value={mode}
            onChange={(e) => setMode((e.target.value as AffiliateCommissionMode) || "")}
            disabled={saving || !userId}
          >
            <option value="">Do not change</option>
            <option value={AffiliateCommissionMode.RAKE}>RAKE</option>
            <option value={AffiliateCommissionMode.NGR}>NGR</option>
            <option value={AffiliateCommissionMode.CPA}>CPA</option>
          </select>

          <div className="text-xs opacity-60">Leave empty to not change</div>
        </div>

        {!isCpaMode ? (
          <div className="flex flex-col gap-1">
            <label className="text-sm opacity-80">Return percent</label>
            <input
              className={clsx("input input-bordered w-full", { "input-error": !!percentError })}
              value={returnPercent}
              onChange={(e) => setReturnPercent(e.target.value)}
              placeholder="0 - 100"
              inputMode="decimal"
              disabled={saving || !userId}
            />
            <div className={clsx("text-xs", percentError ? "text-error" : "opacity-60")}>{percentError ?? "Leave empty to not change"}</div>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <label className="text-sm opacity-80">Reward (cents) {formatFiatCurrency(cpaRewardCents)}</label>
            <input
              className={clsx("input input-bordered w-full", { "input-error": !!rewardError })}
              value={cpaRewardCents}
              onChange={(e) => setCpaRewardCents(e.target.value)}
              placeholder="e.g. 500 (= $5.00)"
              inputMode="numeric"
              disabled={saving || !userId}
            />
            <div className={clsx("text-xs", rewardError ? "text-error" : "opacity-60")}>{rewardError ?? "Leave empty to not change"}</div>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-sm opacity-80">Preview</label>
          <div className="rounded-lg border border-[rgba(0,0,0,0.08)] dark:border-[rgba(255,255,255,0.08)] p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="opacity-70">commission_mode</span>
              <span className="font-medium">{patch.commission_mode ?? "—"}</span>
            </div>

            {!isCpaMode ? (
              <div className="flex items-center justify-between mt-1">
                <span className="opacity-70">return_percent</span>
                <span className="font-medium">{patch.return_percent ?? "—"}</span>
              </div>
            ) : (
              <div className="mt-2">
                <div className="opacity-70 mb-1">cpa_config</div>
                <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(patch.cpa_config ?? null, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>

        {isCpaMode ? (
          <>
            <div className="flex flex-col gap-1">
              <label className="text-sm opacity-80">Require KYC</label>
              <select
                className="select select-bordered w-full"
                value={cpaRequireKyc}
                onChange={(e) => setCpaRequireKyc(e.target.value as any)}
                disabled={saving || !userId}
              >
                <option value="any">Any</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
              <div className="text-xs opacity-60">Eligibility condition</div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm opacity-80">Min total deposit (cents) {formatFiatCurrency(cpaMinDepositCents)}</label>
              <input
                className="input input-bordered w-full"
                value={cpaMinDepositCents}
                onChange={(e) => setCpaMinDepositCents(e.target.value)}
                inputMode="numeric"
                placeholder="e.g. 1000 (= $10.00)"
                disabled={saving || !userId}
              />
              <div className="text-xs opacity-60">Leave empty to not change</div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm opacity-80">Min total wager (cents) {formatFiatCurrency(cpaMinWagerCents)}</label>
              <input
                className="input input-bordered w-full"
                value={cpaMinWagerCents}
                onChange={(e) => setCpaMinWagerCents(e.target.value)}
                inputMode="numeric"
                placeholder="e.g. 10000 (= $100.00)"
                disabled={saving || !userId}
              />
              <div className="text-xs opacity-60">Leave empty to not change</div>
            </div>
          </>
        ) : null}
      </div>

      {!userId ? <div className="mt-3 text-sm text-warning">Missing userId</div> : null}
    </div>
  )
}
